import { useEffect, useRef, useState } from "react";
import { Bell, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { notificationTextClass, notificationHtml } from "@/components/NotificationRichText";

const Banner = ({ item }) => {
  const ref = useRef(null);
  useEffect(() => {
    let recorded = false;
    const observer = new IntersectionObserver(async ([entry]) => {
      if (entry.isIntersecting && !document.hidden && !recorded) {
        recorded = true;
        try { await api.post(`/notifications/${item.id}/view`); }
        catch { recorded = false; }
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [item.id]);
  const urgent = item.priority === "urgent";
  const Icon = urgent ? AlertTriangle : Bell;
  return <article ref={ref} data-testid={`notification-banner-${item.id}`} data-priority={urgent ? "urgent" : "routine"} className={`flex items-start gap-3 px-4 md:px-8 py-3 text-white border-b ${urgent ? "bg-red-700 border-red-600" : "bg-blue-700 border-blue-600"}`}>
    <Icon size={19} className="mt-0.5 shrink-0" aria-hidden="true" />
    <div className="min-w-0 flex-1"><div className="flex flex-wrap justify-between gap-x-4 gap-y-1"><h2 className="min-w-0 [overflow-wrap:anywhere] font-semibold text-sm" data-testid={`notification-banner-title-${item.id}`}><span className="mr-2 inline-block rounded border border-white/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wider" data-testid={`notification-banner-priority-${item.id}`}>{urgent ? "Urgent" : "Routine"}</span>{item.title}</h2><span className={`min-w-0 [overflow-wrap:anywhere] text-xs ${urgent ? "text-red-100" : "text-blue-100"}`} data-testid={`notification-banner-expiry-${item.id}`}>Until {new Date(item.ends_at).toLocaleString()}</span></div>
      <div className={`text-sm mt-1 ${notificationTextClass}`} data-testid={`notification-banner-content-${item.id}`} dangerouslySetInnerHTML={{ __html: notificationHtml(item.content_html, `notification-banner-${item.id}`) }} />
    </div>
  </article>;
};

export const NotificationBanners = () => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    let boundary, poll, stampPoll, running = false;
    let serverOffset = 0;
    let lastStamp = null, again = false;
    const load = async () => {
      if (controller.signal.aborted) return;
      if (running) { again = true; return; }
      running = true;
      setItems((old) => old.filter((item) => Date.parse(item.ends_at) > Date.now() + serverOffset));
      try {
        const { data } = await api.get("/notifications/active", { signal: controller.signal, timeout: 15000 });
        serverOffset = Date.parse(data.server_time) - Date.now();
        setItems(data.items); setError(false);
        clearTimeout(boundary);
        if (data.next_change_at) boundary = setTimeout(load, Math.min(2147483647, Math.max(50, Date.parse(data.next_change_at) - Date.parse(data.server_time) + 50)));
      } catch { if (!controller.signal.aborted) setError(true); }
      finally { running = false; if (again) { again = false; load(); } }
    };
    const checkStamp = async () => {
      if (document.hidden || controller.signal.aborted) return;
      try {
        const { data } = await api.get("/notifications/active/stamp", { signal: controller.signal, timeout: 10000 });
        if (lastStamp !== null && data.stamp !== lastStamp) load();
        lastStamp = data.stamp;
      } catch { /* full refresh below still runs */ }
    };
    const onVisible = () => { if (!document.hidden) load(); };
    load(); checkStamp();
    // Cheap change check so edits/withdrawals reach every open dashboard within seconds.
    stampPoll = setInterval(checkStamp, 5000);
    poll = setInterval(onVisible, 30000);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("notifications-updated", load);
    return () => { controller.abort(); clearTimeout(boundary); clearInterval(poll); clearInterval(stampPoll); document.removeEventListener("visibilitychange", onVisible); window.removeEventListener("notifications-updated", load); };
  }, []);
  return <>
    {items.length > 0 && <section role="region" aria-label="Member notifications" aria-live="polite" className="max-h-[35vh] overflow-y-auto overscroll-contain shadow-sm" data-testid="notifications-banner-stack">{items.map((item) => <Banner key={item.id} item={item} />)}</section>}
    {error && <p role="status" className="px-4 py-1 text-xs bg-muted text-muted-foreground" data-testid="notification-banner-load-error">Notifications are temporarily unavailable. Retrying automatically.</p>}
  </>;
};
